import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Eye, EyeOff, AlertCircle, Lock } from 'lucide-react';
import api from '../components/api';
import useGlobale from '../components/global';
import { toast } from 'react-toastify';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useGlobale();
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Effacer l'erreur pour ce champ lorsqu'il est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[name];
                return newErrors;
            });
        }
        
        // Effacer l'erreur globale quand l'utilisateur commence à taper
        if (globalError) {
            setGlobalError('');
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!formData.username.trim()) {
            newErrors.username = "L'adresse email est requise";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
            newErrors.username = "Veuillez entrer une adresse email valide";
            isValid = false;
        }

        if (!formData.password) {
            newErrors.password = "Le mot de passe est requis";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Effacer les erreurs précédentes avant de soumettre
        setGlobalError('');
        setLoading(true);

        try {
            // Appel API pour l'authentification
            const response = await api.post('utilisateur/signin/', {
                username: formData.username,
                password: formData.password
            });

            // Gérer la réponse de connexion
            if (response.data && response.data.tokens) {
                // Stocker les informations et connecter l'utilisateur
                await login(response.data.tokens, response.data.user);
                toast.success('Connexion réussie!');
                navigate('/');
            } else {
                setGlobalError('Format de réponse invalide du serveur');
            }
        } catch (err) {
            console.error('Erreur de connexion:', err);
            
            // Gestion des erreurs de validation du backend
            if (err.response) {
                const { status, data } = err.response;
                
                // Erreur 404 - Endpoint non trouvé
                if (status === 404) {
                    setGlobalError('Service de connexion non disponible. Veuillez contacter l\'administrateur.');
                }
                // Erreur 401 - Non autorisé (identifiants incorrects)
                else if (status === 401) {
                    setGlobalError('Identifiants incorrects. Veuillez vérifier votre email et mot de passe.');
                }
                // Erreur 400 - Erreurs de validation
                else if (status === 400 && typeof data === 'object') {
                    setErrors(data);
                }
                // Erreur 500 - Erreur serveur
                else if (status >= 500) {
                    setGlobalError('Erreur serveur. Veuillez réessayer plus tard.');
                }
                // Autres erreurs avec message d'erreur
                else if (data && data.error) {
                    setGlobalError(data.error);
                }
                // Autres erreurs avec message de détail
                else if (data && data.detail) {
                    setGlobalError(data.detail);
                }
                // Erreur générique
                else {
                    setGlobalError(`Erreur de connexion (${status}). Veuillez réessayer.`);
                }
            } else if (err.request) {
                // La requête a été faite mais pas de réponse reçue (serveur inaccessible)
                setGlobalError('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
            } else {
                // Erreur de configuration de la requête
                setGlobalError('Erreur lors de la connexion. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    const getFieldError = (fieldName) => {
        return errors[fieldName] ? (
            Array.isArray(errors[fieldName]) 
                ? errors[fieldName][0] 
                : errors[fieldName]
        ) : null;
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-lg w-full bg-white p-12 rounded-3xl shadow-2xl border border-gray-100">
                <div className="mb-8">
                    <img
                        className="mx-auto h-16 w-auto"
                        src="/fonap-lo.png"
                        alt="Fonap Logo"
                    />
                    <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
                        Administration FONAP
                    </h2>
                    <p className="mt-2 text-center text-base text-gray-600">
                        Connectez-vous à votre compte administrateur
                    </p>
                </div>
                
                {/* Affichage de l'erreur globale */}
                {globalError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                            <p className="text-sm text-red-600">{globalError}</p>
                        </div>
                    </div>
                )}

                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-5">
                        {/* Champ d'email */}
                        <div>
                            <label htmlFor="username" className="block text-base font-medium text-gray-700 mb-3">
                                Adresse email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Mail className="h-6 w-6 text-gray-400" />
                                </div>
                                <input
                                    id="username"
                                    name="username"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className={`appearance-none block w-full px-4 py-4 pl-14 border ${getFieldError('username') ? 'border-red-500' : 'border-gray-300'} rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base transition-all duration-200`}
                                    placeholder="admin@fonap.com"
                                    value={formData.username}
                                    onChange={handleChange}
                                />
                            </div>
                            {getFieldError('username') && (
                                <p className="mt-2 text-sm text-red-600">{getFieldError('username')}</p>
                            )}
                        </div>

                        {/* Champ de mot de passe */}
                        <div>
                            <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-3">
                                Mot de passe <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Lock className="h-6 w-6 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    className={`appearance-none block w-full px-4 py-4 pl-14 border ${getFieldError('password') ? 'border-red-500' : 'border-gray-300'} rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base transition-all duration-200`}
                                    placeholder="••••••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-6 w-6" />
                                        ) : (
                                            <Eye className="h-6 w-6" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            {getFieldError('password') && (
                                <p className="mt-2 text-sm text-red-600">{getFieldError('password')}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                className="h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                            />
                            <label htmlFor="remember-me" className="ml-3 block text-base text-gray-900">
                                Se souvenir de moi
                            </label>
                        </div>

                        <div className="text-base">
                            <a href="#" className="font-medium text-orange-600 hover:text-orange-500 transition-colors duration-200">
                                Mot de passe oublié?
                            </a>
                        </div>
                    </div>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white ${
                                loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Connexion en cours...
                                </>
                            ) : (
                                <>
                                    <Lock className="h-5 w-5 mr-2" />
                                    Se connecter
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LoginPage;
